import React, { useEffect, useState } from 'react'
import { injectReducer } from 'store'
import reducer from './store'
import { useDispatch, useSelector } from 'react-redux'
import { getData } from './store/dataSlice'
import { createApiRecord, deleteApiRecord, updateApiRecord } from 'services/DashboardService'
import { Success } from 'utils/alerts'
import { Field, Form, Formik } from 'formik'
import { Button, Select, TextInput } from 'components/Form'
import { ConfirmationModal, Modal, Tooltip } from 'components/Ui'
import * as Yup from 'yup'
import { AiOutlinePlus } from 'react-icons/ai'
import { gridOptions } from 'utils/options'
import { HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi'
import useAuth from 'utils/hooks/useAuth'
injectReducer('performanceDashboard', reducer)

const validationSchema = Yup.object().shape({
    company: Yup.string().required('company field is required'),
    // customer: Yup.string().required('customer field is required'),
    report_url: Yup.string().required('report url field is required'),
    grid: Yup.string().required('grid field is required'),
})

export default function PerformanceDashboards() {
    const dispatch = useDispatch()
    const { checkPermission } = useAuth()
    const { view, data, tableData, loading } = useSelector(state => state.performanceDashboard.data)
    const { company, customer } = useSelector(state => state.filterData)
    const { companyData, customersData } = useSelector(state => state.listData)
    const [selectedData, setSelectedData] = useState()
    const [open, setOpen] = useState(false)

    useEffect(() => {
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [company, customer])

    const fetchData = () => dispatch(getData({ ...tableData, company: company, customer: customer }))

    const handleDelete = id => {
        deleteApiRecord(id)
            .then(() => {
                Success('Deleted Successfully!')
                fetchData()
            })
            .catch(error => console.log('Error: ', error))
    }

    const handleSubmit = data => {
        if (selectedData) {
            updateApiRecord(selectedData?.id, data)
                .then(() => {
                    fetchData()
                    Success('Updated Successfully!')
                    setOpen(false)
                    setSelectedData()
                })
                .catch(error => console.log('Error: ', error))
        } else {
            createApiRecord(data)
                .then(() => {
                    fetchData()
                    Success('Created Successfully!')
                    setOpen(false)
                    setSelectedData()
                })
                .catch(error => console.log('Error: ', error))
        }
    }

    if (loading) {
        return <div>Loading...</div>
    }

    return (
        <>
            <div className="flex flex-col gap-1 mb-5 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="font-semibold text-black text-title-md2 dark:text-white">Dashboards-Performance</h2>
                {checkPermission('performance-dashboard', 'create') ? (
                    <Button
                        type="button"
                        onClick={() => setOpen(true)}
                        className="text-white border-[#F05540] bg-[#F05540] dark:border-[#F05540] dark:bg-[#F05540]"
                    >
                        <AiOutlinePlus size={20} /> Add New
                    </Button>
                ) : null}
            </div>
            <div className="grid grid-cols-12 gap-6">
                {data.map(
                    item =>
                        item.report_url && (
                            <div className={item.grid} key={item.id}>
                                <div className="flex items-center justify-end gap-3">
                                    {checkPermission('performance-dashboard', 'edit') ? (
                                        <Tooltip title={'Edit'}>
                                            <span
                                                onClick={() => {
                                                    setSelectedData(item)
                                                    setOpen(true)
                                                }}
                                                className="hover:text-primary"
                                            >
                                                <HiOutlinePencil size={20} />
                                            </span>
                                        </Tooltip>
                                    ) : null}
                                    {checkPermission('performance-dashboard', 'delete') ? (
                                        <Tooltip title={'Delete'}>
                                            <ConfirmationModal
                                                message={'Are you sure you want to delete this frame!'}
                                                onConfirm={() => handleDelete(item.id)}
                                            >
                                                <span className="hover:text-danger">
                                                    <HiOutlineTrash size={20} />
                                                </span>
                                            </ConfirmationModal>
                                        </Tooltip>
                                    ) : null}
                                </div>
                                <iframe
                                    id="dashboardFrame"
                                    className={`dashboard-iframe w-full h-[100vh]`}
                                    src={item.report_url}
                                    title="Dashboard"
                                ></iframe>
                            </div>
                        )
                )}
            </div>
            <Modal
                show={open}
                maxWidth="4xl"
                onClose={() => {
                    setOpen(false)
                    setSelectedData()
                }}
            >
                <div className="flex flex-col gap-1 m-3">
                    <h2 className="font-semibold text-black text-title-md">{selectedData ? 'Update Frame' : 'Add New Frame'}</h2>
                </div>
                <div className="flex flex-col items-center">
                    <div className="w-full bg-white border rounded-sm overflow-auto max-h-150 border-stroke shadow-default dark:border-strokedark dark:bg-boxdark">
                        <div className="flex flex-col gap-3 p-6">
                            <Formik
                                initialValues={selectedData ? selectedData : view}
                                validationSchema={validationSchema}
                                onSubmit={values => {
                                    handleSubmit(values)
                                }}
                            >
                                {({ values, touched, errors }) => (
                                    <Form>
                                        <div className="grid grid-cols-1 gap-x-4 gap-y-4 md:grid-cols-4">
                                            <Field name="company">
                                                {({ field, form }) => (
                                                    <Select
                                                        id="company"
                                                        name="company"
                                                        label="Company"
                                                        placeholder="Company"
                                                        className="md:col-span-2"
                                                        value={values?.company}
                                                        options={companyData}
                                                        onChange={e => {
                                                            form.setFieldValue(field.name, e.target.value)
                                                            form.setFieldValue('customer', '')
                                                        }}
                                                        error={touched.company && errors.company}
                                                        message={errors.company}
                                                    />
                                                )}
                                            </Field>
                                            <Field name="customer">
                                                {({ field, form }) => (
                                                    <Select
                                                        id="customer"
                                                        name="customer"
                                                        label="Customer"
                                                        placeholder="Customer"
                                                        className="md:col-span-2"
                                                        value={values?.customer}
                                                        options={customersData?.filter(
                                                            item => item?.company?.id?.toString() === values?.company?.toString()
                                                        )}
                                                        onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                        error={touched.customer && errors.customer}
                                                        message={errors.customer}
                                                    />
                                                )}
                                            </Field>
                                            <Field name="report_url">
                                                {({ field, form }) => (
                                                    <TextInput
                                                        type="text"
                                                        label="Report Url"
                                                        name="report_url"
                                                        className="md:col-span-2"
                                                        error={errors.report_url && touched.report_url}
                                                        message={errors.report_url}
                                                        value={values?.report_url}
                                                        onChange={e => {
                                                            const url = e.target.value
                                                            form.setFieldValue(field.name, url)
                                                        }}
                                                    />
                                                )}
                                            </Field>
                                            <Field name="grid">
                                                {({ field, form }) => (
                                                    <Select
                                                        id="grid"
                                                        name="grid"
                                                        label="Grid"
                                                        placeholder="Grid"
                                                        className="md:col-span-2"
                                                        value={values?.grid}
                                                        options={gridOptions}
                                                        onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                        error={touched.grid && errors.grid}
                                                        message={errors.grid}
                                                    />
                                                )}
                                            </Field>
                                            <Field name="description">
                                                {({ field, form }) => (
                                                    <TextInput
                                                        type="text"
                                                        label="Description"
                                                        name="description"
                                                        className="md:col-span-2"
                                                        error={errors.description && touched.description}
                                                        message={errors.description}
                                                        value={values?.description}
                                                        onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                    />
                                                )}
                                            </Field>
                                        </div>
                                        <div className="flex items-center justify-end gap-4 mt-3">
                                            <Button
                                                className="h-10 w-18 text-primary border border-primary dark:text-secondary dark:border-secondary"
                                                type="button"
                                                onClick={() => {
                                                    setOpen(false)
                                                    setSelectedData()
                                                }}
                                            >
                                                Discard
                                            </Button>
                                            <Button
                                                type="submit"
                                                className="flex items-center justify-center px-6 py-2 text-white transition border rounded-lg cursor-pointer hover:bg-opacity-90 border-primary bg-primary dark:border-secondary dark:bg-secondary"
                                            >
                                                Save
                                            </Button>
                                        </div>
                                    </Form>
                                )}
                            </Formik>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    )
}
